import { useState } from 'react';
import { mutations } from 'entities';

import { Box, Button, Column, FieldError } from 'components/Blocks';
import { getFormConfig } from 'components/Form';
import { LockIcon, RefreshIcon } from 'components/Blocks/Icons';

import { navigateToLogin } from 'providers/Auth';

import { useLang } from 'locale';
import { v } from 'utils/validation';

import { OTPInput } from './OTPInput';
import { PasswordInput } from './PasswordInput';
import { useToggleV2 } from 'hooks';

const codeStep = getFormConfig({
  email: v.common.email(),
  code: v.string().min(6).max(6),
  password: v.string().min(3),
  confirmPassword: v.string().min(3),
}).refine((schema) =>
  schema.refine((data) => data.password === data.confirmPassword, {
    message: 'pw-no-match',
    path: ['confirmPassword'],
  }),
);

const SHOW_RESENT = {
  multiple: 3,
  max: 10,
};

export function OTPStep({ email }: { email: string }) {
  const { t } = useLang();

  const [errorCount, setErrorCount] = useState(0);

  const [showCodeError, codeError] = useToggleV2();

  const finish = mutations.auth.resetPasswordByCode.use({
    onSuccess: () => {
      navigateToLogin({ emailPasswordReset: email });
    },

    onError: () => {
      codeError.turnOn();
      setErrorCount((old) => old + 1);
    },
  });

  const forgotPW = mutations.auth.forgotPassword.use({
    params: { email },
    onSuccess: () => {
      setErrorCount((old) => old + 1);
    },
  });

  const allowResend =
    errorCount && !(errorCount % SHOW_RESENT.multiple) && errorCount < SHOW_RESENT.max;

  return (
    <codeStep.Form
      onSubmit={finish.execute}
      onBeforeSubmit={codeError.turnOff}
      defaultValues={{ email }}
    >
      <Box className="mx-auto bg-primary/20 p-2 text-primary" border="circle">
        <LockIcon size={18} />
      </Box>

      <h1 className="text-2xl text-foreground-title phone:text-xl">{t('set-new-password')}</h1>

      <p className="mx-auto max-w-[80%] overflow-hidden text-foreground-light phone:text-base">
        {t('code-sent-to-email')}{' '}
        <strong className="inline-block max-w-full truncate font-medium text-primary">
          {email}
        </strong>
      </p>

      <Column gap="6">
        <div className="my-2">
          <codeStep.Controller
            property="code"
            render={({ field, fieldState }) => (
              <OTPInput hasError={!!fieldState.error || showCodeError} onChange={field.onChange} />
            )}
          />
        </div>

        {showCodeError && <FieldError error="invalid-code" className="-mt-5" />}

        <PasswordInput form={codeStep} name="password" />

        <PasswordInput form={codeStep} name="confirmPassword" />

        <codeStep.SubmitButton className="mt-2 h-auto py-2">
          {t('change-password')}
        </codeStep.SubmitButton>

        <p className="mx-auto max-w-[90%] text-xs text-foreground-light/70">
          {t('check-spam-folder')}
        </p>

        {!!allowResend && (
          <Button variant="link" className="text-foreground-title" {...forgotPW.buttonProps}>
            <RefreshIcon />
            {t('resend')}
          </Button>
        )}
      </Column>
    </codeStep.Form>
  );
}
